import { atom } from 'jotai';
import { selectAtom } from 'jotai/utils';

import { DisplayRef, EquipmentDetailsResponse, Setting } from '@repo/sdk/models/components';

import { EnhancedSetting } from '@/features/equipment-browser/subscriptions/use-get-settings-values';
import { settingsFamily } from '@/hooks/query-hooks/streams/gaia-api/use-settings-stream';

import { equipmentResponseAtom } from './equipment-response.state';

export const buildEnhancedSetting = (
  setting: DisplayRef,
  settingValue: Setting
): EnhancedSetting => {
  return {
    ...setting,
    key: setting?.ref?.id,
    value: settingValue?.value?.[settingValue?.kind as keyof typeof settingValue.value],
    units: setting?.ref?.units,
  };
};

/**
 * displaySettingsListAtom
 * maintains the list of enhanced settings for the display settings
 *
 * Builds the list of enhanced settings for the display settings
 * by mapping the display settings to the enhanced settings
 *
 * Possible future optimizations: convert this to a splitAtom to avoid re-rendering the entire list when a single setting changes
 */
export const displaySettingsListAtom = atom<EnhancedSetting[]>((get) => {
  const equipment: EquipmentDetailsResponse | undefined = get(equipmentResponseAtom);

  return (
    equipment?.displaySettings?.map((setting: DisplayRef): EnhancedSetting => {
      const settingValue = get(settingsFamily({ settingId: setting?.ref?.id } as Setting));

      return buildEnhancedSetting(setting, settingValue);
    }) ?? []
  );
});
displaySettingsListAtom.debugLabel = 'displaySettingsListAtom';

/**
 * getDisplaySettingAtom
 * returns an atom for retrieving a specific setting from the display settings list
 *
 * @param id - The unique identifier of the setting to retrieve
 * @returns A select atom that returns the matching enhanced setting and only re-renders when
 *          the setting's value or label changes
 */
export const getDisplaySettingAtom = (id: string) => {
  const settingAtom = atom((get) => {
    const settings = get(displaySettingsListAtom);

    return settings?.find((setting) => setting?.ref?.id === id);
  });

  return selectAtom(
    settingAtom,
    (atom) => atom,
    (a, b) => {
      return a?.value === b?.value && a?.label === b?.label;
    }
  );
};
getDisplaySettingAtom.debugLabel = 'getDisplaySettingAtom';
